import React from 'react';
import { Layout, Input, Badge, Space, Typography, Button, Tag, Modal, Form, message, Dropdown, List } from 'antd';
import { DoubleLeftOutlined, DoubleRightOutlined, BellOutlined, LogoutOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import apiClient from '../services/api';
import GlobalSearch from './GlobalSearch';
import { getSoonDueMaintenances7, getSoonDueMaintenances30, getOverdueMaintenances, getOverdueAuditAssets, getSoonAuditAssets7, getSoonAuditAssets30 } from '../services/api';
import AssetDetailModal from './AssetDetailModal';
import dayjs from 'dayjs';

const { Header } = Layout;
const { Text } = Typography;

interface AppHeaderProps {
  onMenuClick?: () => void;
  collapsed?: boolean;
}

const roleMap: Record<string, string> = {
  super_admin: 'Super Admin',
  company_admin: 'Quản lý công ty',
  manager: 'Chuyên viên tài sản',
  employee_user: 'Nhân viên',
};

const AppHeader: React.FC<AppHeaderProps> = ({ onMenuClick, collapsed }) => {
  const { user, logout } = useAuth();
  const [pwdModal, setPwdModal] = React.useState(false);
  const [pwdForm] = Form.useForm();
  const [pwdLoading, setPwdLoading] = React.useState(false);

  // Notification state
  const [notifications, setNotifications] = React.useState<any[]>([]);
  const [assetDetailModalOpen, setAssetDetailModalOpen] = React.useState(false);
  const [selectedAssetId, setSelectedAssetId] = React.useState<number | null>(null);

  React.useEffect(() => {
    // Chỉ gọi API khi user đã đăng nhập
    if (user) {
      Promise.all([
        getOverdueMaintenances(),
        getSoonDueMaintenances7(),
        getSoonDueMaintenances30(),
        getOverdueAuditAssets(),
        getSoonAuditAssets7(),
        getSoonAuditAssets30()
      ]).then(([
        overdueMaint,
        soonMaint7,
        soonMaint30,
        overdueAudit,
        soonAudit7,
        soonAudit30
      ]) => {
        const notificationsArr: any[] = [];
        (overdueMaint as any[]).forEach(item => notificationsArr.push({ ...item, type: 'maintenance', group: 'overdue' }));
        (soonMaint7 as any[]).forEach(item => notificationsArr.push({ ...item, type: 'maintenance', group: 'soon7' }));
        (soonMaint30 as any[]).forEach(item => notificationsArr.push({ ...item, type: 'maintenance', group: 'soon30' }));
        (overdueAudit as any[]).forEach(item => notificationsArr.push({ ...item, type: 'audit', group: 'overdue' }));
        (soonAudit7 as any[]).forEach(item => notificationsArr.push({ ...item, type: 'audit', group: 'soon7' }));
        (soonAudit30 as any[]).forEach(item => notificationsArr.push({ ...item, type: 'audit', group: 'soon30' }));
        setNotifications(notificationsArr);
      }).catch((error) => {
        // Bỏ qua lỗi nếu không thể load notifications
        console.log('Không thể load notifications:', error);
        setNotifications([]);
      });
    }
  }, [user]);

  const notificationMenu = {
    items: notifications.length > 0 ? notifications.map((item, idx) => ({
      key: `${item.type}-${item.group}-${item.asset_id || item.name || idx}`,
      label: (
        <div>
          {item.type === 'maintenance' ? (
            <>
              <b>Bảo dưỡng</b> {item.group === 'overdue' ? <span style={{ color: '#ff4d4f' }}>(Quá hạn)</span> : item.group === 'soon7' ? <span style={{ color: '#faad14' }}>(7 ngày)</span> : <span style={{ color: '#1890ff' }}>(30 ngày)</span>}
              : {item.asset_code || `ID: ${item.asset_id}`} - Ngày: {item.next_due_date ? dayjs(item.next_due_date).format('DD/MM/YYYY') : ''}
            </>
          ) : (
            <>
              <b>Kiểm kê</b> {item.group === 'overdue' ? <span style={{ color: '#ff4d4f' }}>(Quá hạn)</span> : item.group === 'soon7' ? <span style={{ color: '#faad14' }}>(7 ngày)</span> : <span style={{ color: '#1890ff' }}>(30 ngày)</span>}
              : {item.name} ({item.asset_code}) - Hạn: {item.next_audit_due ? dayjs(item.next_audit_due).format('DD/MM/YYYY') : ''}
            </>
          )}
        </div>
      )
    })) : [],
    style: { minWidth: 320, maxHeight: 400, overflow: 'auto' }
  };

  const handleChangePwd = async () => {
    try {
      setPwdLoading(true);
      const values = await pwdForm.validateFields();
      await apiClient.changeUserPassword(
        user!.id,
        values.oldPassword,
        values.newPassword
      );
      message.success('Đổi mật khẩu thành công!');
      setPwdModal(false);
      pwdForm.resetFields();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Đổi mật khẩu thất bại!';
      message.error(msg);
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <Header style={{ background: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', height: 64 }}>
      {onMenuClick && (
        collapsed ? (
          <DoubleRightOutlined style={{ fontSize: 22, marginRight: 24, cursor: 'pointer' }} onClick={onMenuClick} />
        ) : (
          <DoubleLeftOutlined style={{ fontSize: 22, marginRight: 24, cursor: 'pointer' }} onClick={onMenuClick} />
        )
      )}
      <div style={{ maxWidth: 350, flex: 1, marginRight: 24 }}>
        <GlobalSearch onAssetSelect={id => { setSelectedAssetId(id); setAssetDetailModalOpen(true); }} />
      </div>
      <div style={{ flex: 1 }} />
      <Space size="large" style={{ marginLeft: 'auto', height: 64, alignItems: 'center' }}>
        <Dropdown menu={notificationMenu} trigger={["click"]} placement="bottomRight">
          <Badge count={notifications.length} size="small">
            <BellOutlined style={{ fontSize: 22, cursor: 'pointer' }} />
          </Badge>
        </Dropdown>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', height: 40, minWidth: 120 }}>
          <Text strong style={{ fontSize: 13, lineHeight: '1.2' }}>{user?.employee_name || user?.username}</Text>
          <Tag color="green" style={{ fontSize: 10, padding: '0 6px', marginTop: 2, marginRight: 0 }}>
            {user?.role === 'employee_user' && user?.managed_location_name
              ? (user.managed_location_ids && user.managed_location_ids.length > 1
                ? `Kiểm soát tài sản [${user.managed_location_name}]`
                : `Kiểm soát tài sản ${user.managed_location_name}`)
              : roleMap[user?.role || '']}
          </Tag>
        </div>
        <Button type="text" icon={<LockOutlined />} onClick={() => setPwdModal(true)} />
        <Button type="text" icon={<LogoutOutlined />} onClick={logout} />
      </Space>
      <Modal
        open={pwdModal}
        title={`Đổi mật khẩu cho ${user?.employee_name || user?.username}`}
        onCancel={() => { setPwdModal(false); pwdForm.resetFields(); }}
        footer={null}
        destroyOnHidden
      >
        <Form form={pwdForm} layout="vertical" onFinish={handleChangePwd}>
          <Form.Item name="oldPassword" label="Mật khẩu cũ" rules={[{ required: true, message: 'Nhập mật khẩu cũ!' }]}>
            <Input.Password placeholder="Mật khẩu cũ" />
          </Form.Item>
          <Form.Item name="newPassword" label="Mật khẩu mới" rules={[{ required: true, message: 'Nhập mật khẩu mới!' }, { min: 8, message: 'Mật khẩu tối thiểu 8 ký tự!' }]}>
            <Input.Password placeholder="Mật khẩu mới" />
          </Form.Item>
          <Form.Item name="confirmPassword" label="Xác nhận mật khẩu mới" dependencies={["newPassword"]} rules={[
            { required: true, message: 'Xác nhận mật khẩu mới!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Mật khẩu xác nhận không khớp!'));
              },
            }),
          ]}>
            <Input.Password placeholder="Xác nhận mật khẩu mới" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" size="large" loading={pwdLoading} style={{ width: '100%', borderRadius: 8, height: 40 }}>Đổi mật khẩu</Button>
          </Form.Item>
        </Form>
      </Modal>
      <AssetDetailModal
        assetId={selectedAssetId}
        visible={assetDetailModalOpen}
        onClose={() => setAssetDetailModalOpen(false)}
      />
    </Header>
  );
};

export default AppHeader; 